import { BinderAmendment } from "@dune/types";
import { WarrantyType } from "@dune/types";
import { Button, FileInput } from "@dune/ui";
import { useState } from "react";
import { P, match } from "ts-pattern";
import { usePricingFileUpload } from "../../../api/hooks/usePricingFileUpload";
import { useGetPricingFileUrlByWarrantyTypeAndBinderAmendmentId } from "../../../api/queries/pricing-files.queries";
import { toast } from "sonner";
import { downloadFileWithName } from "../../../utils/document.utils";

const BinderPricing = ({
	binderAmendment,
}: {
	binderAmendment: BinderAmendment;
}) => (
	<div className="flex flex-col gap-4 w-full">
		{binderAmendment.binderWarranties
			.filter((binderWarranty) => binderWarranty.warrantyType !== null)
			.filter((binderWarranty) => binderWarranty.dependsOn === null)
			.map((binderWarranty) => {
				if (!binderWarranty.warrantyType) {
					toast.error("Erreur: warrantyType est null");
					console.error("Erreur: warrantyType est null");
					return null;
				}
				return (
					<WarrantyPricing
						key={binderWarranty.id}
						name={match([binderWarranty.name, binderWarranty.shortName])
							.with(
								[P.any, P.not(P.nullish)],
								([, shortName]) =>
									`${shortName} (${binderWarranty.warrantyType})`,
							)
							.with(
								[P.not(P.nullish), P.any],
								([name]) => `${name} (${binderWarranty.warrantyType})`,
							)
							.otherwise(() => `${binderWarranty.warrantyType}`)}
						type={binderWarranty.warrantyType}
						binderAmendmentId={binderAmendment.id}
					/>
				);
			})}
	</div>
);

interface WarrantyPricingProps {
	name: string;
	type: WarrantyType;
	binderAmendmentId: number;
}

const WarrantyPricing = ({
	name,
	type,
	binderAmendmentId,
}: WarrantyPricingProps) => {
	const [pricingFile, setPricingFile] = useState<File | undefined>(undefined);
	const { uploadPricingFile, isUploading } = usePricingFileUpload();

	const { data: existingPricingFile } =
		useGetPricingFileUrlByWarrantyTypeAndBinderAmendmentId(
			type,
			binderAmendmentId,
		);

	const handleImport = async () => {
		if (!pricingFile) return;

		try {
			await uploadPricingFile(pricingFile, type, binderAmendmentId);
			setPricingFile(undefined);
		} catch (error) {
			console.error("Error uploading pricing file:", error);
		}
	};

	const handleDownload = async () => {
		if (existingPricingFile?.url) {
			await downloadFileWithName(
				existingPricingFile.url,
				existingPricingFile.pricingFile.file.name,
			);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<h1 className="text-[#144C65] text-2xl font-medium">{name}</h1>
			<div className="flex flex-row gap-4 w-full border-solid border-1 border-gray-200 rounded-lg p-6">
				<h2 className="text-[#144C65] text-l font-medium min-w-44">
					Fichier de tarification
				</h2>
				<div className="flex flex-row gap-4 w-full">
					<FileInput
						className="w-full"
						variant={existingPricingFile?.url ? "secondary" : "default"}
						onFileLoad={setPricingFile}
						file={pricingFile}
						acceptedExtensions={[".xls", ".xlsx"]}
					/>
					<Button disabled={!pricingFile || isUploading} onClick={handleImport}>
						{isUploading ? "Import en cours..." : "Importer"}
					</Button>
					<Button
						variant="secondary"
						disabled={!existingPricingFile?.url}
						onClick={handleDownload}
					>
						Télécharger
					</Button>
				</div>
			</div>
		</div>
	);
};

export default BinderPricing;
